import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchCreditBalance,
  formatCents,
  HUB_UPGRADE_URL,
  isFreePlan,
  maxUsagePct,
  planDisplayName,
  setOverage,
  startTopup,
} from '../lib/creditBalance';

function UsageRow({ label, used, included, pct, warn }) {
  const unlimited = included == null;
  const usedN = Number(used) || 0;
  const capLabel = unlimited ? `${usedN} · unlimited` : `${usedN} / ${included}`;
  const width = unlimited ? 0 : Math.min(100, Math.max(0, Number(pct) || 0));
  const color = warn && width >= 100 ? 'var(--truva-danger-red)' : warn && width >= 80 ? 'var(--truva-warn-amber)' : 'var(--truva-action-blue)';
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--truva-text-secondary)', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: '0.95rem', color: 'var(--truva-text)' }}>{capLabel}</span>
      </div>
      {!unlimited && (
        <div style={{ height: 6, borderRadius: 99, background: 'var(--truva-border)', overflow: 'hidden' }}>
          <span style={{ display: 'block', height: '100%', width: `${width}%`, background: color, borderRadius: 99 }} />
        </div>
      )}
    </div>
  );
}

export default function CreditUsage({ authToken, canManageBilling = false }) {
  const [bal, setBal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [billingMsg, setBillingMsg] = useState('');
  const [limitDollars, setLimitDollars] = useState('');

  const loadBalance = useCallback(async (isCancelled = () => false) => {
    try {
      const data = await fetchCreditBalance(authToken);
      if (isCancelled() || !data || data.ok === false) return;
      if (data.plan == null && data.month_minutes == null) return;
      setBal(data);
    } catch (_) { /* usage chip is best-effort */ }
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return undefined;
    let cancelled = false;
    loadBalance(() => cancelled);
    return () => { cancelled = true; };
  }, [authToken, loadBalance]);

  const onTopup = async () => {
    setBusy(true);
    setBillingMsg('');
    try {
      const res = await startTopup(authToken);
      if (res.ok && res.url) {
        window.open(res.url, '_blank', 'noopener,noreferrer');
        setBillingMsg('Checkout opened. Your credit updates once payment completes.');
      } else {
        setBillingMsg(res.error || 'Could not start top-up. Try again shortly.');
      }
    } catch (_) {
      setBillingMsg('Could not start top-up. Try again shortly.');
    } finally {
      setBusy(false);
    }
  };

  const onToggleOverage = async (enabled) => {
    setBusy(true);
    setBillingMsg('');
    try {
      const dollars = parseFloat(limitDollars);
      const limitCents = enabled && Number.isFinite(dollars) && dollars > 0 ? Math.round(dollars * 100) : null;
      const res = await setOverage(authToken, enabled, limitCents);
      if (!res.ok) setBillingMsg(res.error || 'Could not update overage setting.');
      await loadBalance();
    } catch (_) {
      setBillingMsg('Could not update overage setting.');
    } finally {
      setBusy(false);
    }
  };

  if (!bal) return null;

  const plan = bal.plan || 'greet';
  const free = isFreePlan(plan);
  const pct = maxUsagePct(bal);
  const approaching = free && pct >= 80;
  const atCap = free && pct >= 100;
  const creditCents = Number(bal.credit_cents) || 0;
  const includedMinutes = bal.included_minutes != null
    ? bal.included_minutes
    : (bal.limits && bal.limits.minutes);
  const includedSms = bal.included_sms != null
    ? bal.included_sms
    : (bal.limits && bal.limits.sms);

  let nudge = null;
  if (approaching) {
    const status = atCap
      ? "You've used this month's free allowance."
      : "You're approaching this month's free allowance.";
    nudge = (
      <div style={{
        marginTop: 4, padding: '8px 10px', borderRadius: 8, fontSize: '0.88rem', lineHeight: 1.45,
        background: atCap ? 'var(--truva-danger-tint-chip)' : 'var(--truva-warn-tint-chip)',
        border: atCap ? '1px solid var(--truva-danger-border)' : '1px solid var(--truva-warn-border)',
        color: atCap ? 'var(--truva-danger-text)' : 'var(--truva-warn-text)',
      }}>
        {status}
        {canManageBilling && (
          <>
            {' '}Add $10 overage or{' '}
            <a href={HUB_UPGRADE_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--truva-action-blue)', fontWeight: 600 }}>
              upgrade
            </a>
            {' '}via Hub.
          </>
        )}
      </div>
    );
  }

  return (
    <section data-testid="credit-usage" style={{
      border: 'none', borderRadius: 'var(--truva-radius-grouped)', padding: '12px var(--truva-list-inset)', margin: '0 var(--truva-list-inset) 12px', background: 'var(--truva-grouped-row-bg)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--truva-text)' }}>Usage</h3>
        <span style={{ fontSize: '0.9rem', color: 'var(--truva-text-secondary)', fontWeight: 600 }}>{planDisplayName(plan)}</span>
      </div>
      <UsageRow
        label="Minutes (all lines)"
        used={bal.month_minutes}
        included={includedMinutes}
        pct={bal.usage_pct_minutes}
        warn={free}
      />
      <UsageRow
        label="SMS"
        used={bal.month_sms}
        included={includedSms}
        pct={bal.usage_pct_sms}
        warn={free}
      />
      {Array.isArray(bal.per_line_minutes) && bal.per_line_minutes.length > 0 && (
        <div style={{ marginBottom: 4, marginTop: 6, paddingTop: 8, borderTop: '1px solid var(--truva-border)' }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--truva-text-tertiary)', fontWeight: 600, marginBottom: 6 }}>
            Usage by line (this month)
          </div>
          {bal.per_line_minutes.map((pl) => (
            <div key={pl.phone_number} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--truva-text-secondary)', padding: '2px 0' }}>
              <span>{pl.phone_number} <span style={{ color: 'var(--truva-text-tertiary)', fontSize: '0.82rem' }}>· {pl.calls} calls</span></span>
              <span style={{ fontWeight: 600, color: 'var(--truva-text)' }}>{Number(pl.minutes).toFixed(1)} min</span>
            </div>
          ))}
        </div>
      )}
      {creditCents > 0 && (
        <p style={{ margin: '2px 0 0', fontSize: '0.9rem', color: 'var(--truva-text)' }}>
          Prepaid credit: <strong>{formatCents(creditCents)}</strong> remaining
        </p>
      )}
      {nudge}
      {canManageBilling && (
        <div data-testid="credit-billing-controls" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--truva-border)' }}>
          <button
            type="button"
            onClick={onTopup}
            disabled={busy}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', fontWeight: 600, background: 'var(--truva-action-blue)', color: '#fff', opacity: busy ? 0.6 : 1 }}
          >
            Add prepaid credit
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: '0.9rem', color: 'var(--truva-text)' }}>
            <input
              type="checkbox"
              checked={!!bal.overage_opted_in}
              disabled={busy}
              onChange={(e) => onToggleOverage(e.target.checked)}
            />
            Keep going past my allowance (billed monthly)
          </label>
          {bal.overage_opted_in && (
            <div style={{ marginTop: 6, fontSize: '0.88rem', color: 'var(--truva-text-secondary)' }}>
              Spend limit: {bal.overage_limit_cents != null ? formatCents(bal.overage_limit_cents) : 'none'}
              {' · '}this month: {formatCents(bal.overage_cost_cents)}
              <div style={{ marginTop: 4 }}>
                <input
                  type="number"
                  min="0"
                  inputMode="decimal"
                  placeholder="New limit in $"
                  value={limitDollars}
                  onChange={(e) => setLimitDollars(e.target.value)}
                  style={{ width: 120, marginRight: 8 }}
                />
                <button type="button" disabled={busy || !limitDollars} onClick={() => onToggleOverage(true)}>
                  Set limit
                </button>
              </div>
            </div>
          )}
          {billingMsg && (
            <p role="status" style={{ margin: '8px 0 0', fontSize: '0.88rem', color: 'var(--truva-text-secondary)' }}>{billingMsg}</p>
          )}
        </div>
      )}
    </section>
  );
}
